
import { Fixture } from "../types";
import { api } from "./apiClient";
import { createCombinedMask, fixtureContributesToCombinedMask } from "../utils/maskGenerator";
import { prePaintLightGuides } from "../utils/imagePreprocessor";
import {
  createOffscreenSpatialMaps,
  getSpatialData,
  type SpatialCoordinateMeta,
} from "../utils/spatialLightingMapping";
import { normalizeStripContext, type StripContext } from "../utils/stripLightContext";

/**
 * 无深度图或采样不可靠时，按 stripContext 注入物理先验法线（与 maskGenerator / 后端 prompt 一致）。
 * ceiling_cove 等：光洗向下方受光面 → N≈(0,-1,0)；立面洗墙：N≈(0,0,1) 朝向相机近似。
 */
function stripContextFallbackNormal(ctx: StripContext): { nx: number; ny: number; nz: number } {
  switch (ctx) {
    case "ceiling_cove":
    case "floating_ceiling":
    case "curtain_pocket":
    case "beam_soften":
    case "ac_edge":
      return { nx: 0, ny: -1, nz: 0 };
    case "wall_feature":
    case "wall_panel":
    case "niche":
    case "door_frame":
    case "corner_guide":
    case "mirror_back":
    case "wardrobe":
      return { nx: 0, ny: 0, nz: 1 };
    case "under_cabinet":
    case "glass_display":
    case "shelf_front":
    case "drawer":
      return { nx: 0, ny: -1, nz: 0 };
    case "skirting":
    case "bed_skirt":
    case "stair_nosing":
    case "floating_cabinet":
    case "bath_tub":
    case "shower_niche":
    case "toilet_guide":
      return { nx: 0, ny: 0, nz: 1 };
    case "stair_handrail":
    case "out_rail":
    case "out_seat":
    case "building_outline":
    case "tree_ring":
    case "generic":
    default:
      return { nx: 0, ny: -1, nz: 0 };
  }
}

function isManualCoveSpatialTarget(f: Fixture): boolean {
  return (
    String((f as any).source || "").toLowerCase() === "manual" &&
    (f.type === "cove_light" || f.type === "cove_lighting")
  );
}

/** 深度极暗或法线退化时，认为采样置信度低，改用法线先验 */
function shouldUseStripFallbackNormal(sample: {
  nx: number;
  ny: number;
  nz: number;
  zNorm: number;
}): boolean {
  if (sample.zNorm < 0.07) return true;
  const len = Math.hypot(sample.nx, sample.ny, sample.nz);
  if (len < 0.35) return true;
  if (Math.abs(sample.ny) < 0.06 && Math.hypot(sample.nx, sample.nz) < 0.45) return true;
  return false;
}

/** 渲染前补全 spatialNormal/spatialZNorm：筒射灯 + cove_light（含手动灯带锚点贴墙/洗地方向） */
async function enrichFixturesWithSpatialSamples(
  fixtures: Fixture[],
  depthUrl: string | null | undefined,
  normalUrl: string | null | undefined,
  meta: SpatialCoordinateMeta | null | undefined,
  ow: number,
  oh: number,
): Promise<Fixture[]> {
  let ctx: Awaited<ReturnType<typeof createOffscreenSpatialMaps>> | null = null;
  if (depthUrl && normalUrl && meta) {
    try {
      ctx = await createOffscreenSpatialMaps(depthUrl, normalUrl, ow, oh, meta);
    } catch (e) {
      console.warn("[前端渲染] 离屏空间结构数据加载失败，手动光带将使用先验法线", e);
    }
  }

  return fixtures.map((f) => {
    const t = String(f.type || "").toLowerCase();
    const spatialTypes =
      t === "spotlight" ||
      t === "downlight" ||
      t === "cove_light" ||
      t === "cove_lighting";
    if (!spatialTypes || !f.isModified) return f;

    const stripCtx = normalizeStripContext((f as any).stripContext);
    const prior = stripContextFallbackNormal(stripCtx);

    if (isManualCoveSpatialTarget(f)) {
      if (!ctx) {
        return {
          ...f,
          spatialNormal: prior,
          spatialZNorm: f.spatialZNorm ?? 0.45,
        };
      }
      const mx = (f as any)._modelNormalizedX ?? f.x;
      const my = (f as any)._modelNormalizedY ?? f.y;
      try {
        const s = getSpatialData(ctx, mx, my);
        if (shouldUseStripFallbackNormal(s)) {
          return {
            ...f,
            spatialNormal: prior,
            spatialZNorm: Math.max(0.22, s.zNorm),
          };
        }
        return {
          ...f,
          spatialNormal: { nx: s.nx, ny: s.ny, nz: s.nz },
          spatialZNorm: s.zNorm,
        };
      } catch {
        return {
          ...f,
          spatialNormal: prior,
          spatialZNorm: f.spatialZNorm ?? 0.45,
        };
      }
    }

    if (!ctx) return f;
    if (f.spatialNormal && f.spatialZNorm !== undefined) return f;
    const mx = (f as any)._modelNormalizedX ?? f.x;
    const my = (f as any)._modelNormalizedY ?? f.y;
    try {
      const s = getSpatialData(ctx, mx, my);
      return {
        ...f,
        spatialNormal: { nx: s.nx, ny: s.ny, nz: s.nz },
        spatialZNorm: s.zNorm,
      };
    } catch {
      return f;
    }
  });
}

export const analyzeImageForFixtures = async (
  base64Image: string,
  taskId?: string | null,
): Promise<{ fixtures: Fixture[]; identifyCount?: number; taskId?: string }> => {
  // 🔥 强制打印，确保能看到
  console.log('🔥🔥🔥 [前端分析] 函数被调用了！Base64长度:', base64Image.length);
  
  try {
    console.log('🔥 [前端分析] 开始调用 api.analyzeImage...');
    const response = await api.analyzeImage(base64Image, taskId ?? undefined);
    
    // 🔥 强制打印，无论什么情况都要打印
    console.log('🔥🔥🔥 ==========================================');
    console.log('🔥🔥🔥 [前端分析] api.analyzeImage 返回结果');
    console.log('🔥🔥🔥 ==========================================');
    console.log('🔥 response:', response);
    console.log('🔥 response类型:', typeof response);
    console.log('🔥 response是否为null:', response === null);
    console.log('🔥 response是否为undefined:', response === undefined);
    console.log('🔥 response是否为对象:', response !== null && typeof response === 'object');
    
    if (response && typeof response === 'object') {
      console.log('🔥 response的键:', Object.keys(response));
      console.log('🔥 是否有fixtures字段:', 'fixtures' in response);
      if ('fixtures' in response) {
        console.log('🔥 fixtures值:', (response as any).fixtures);
        console.log('🔥 fixtures类型:', typeof (response as any).fixtures);
        console.log('🔥 fixtures是否为数组:', Array.isArray((response as any).fixtures));
        if (Array.isArray((response as any).fixtures)) {
          console.log('🔥 fixtures长度:', (response as any).fixtures.length);
          console.log('🔥 fixtures内容:', (response as any).fixtures);
        }
      }
    }
    console.log('🔥🔥🔥 ==========================================');
    
    // 🔥 核心修复：直接提取fixtures，确保不会丢失
    let fixtures: Fixture[] = [];
    
    // 方式1：直接访问 response.fixtures（最常见的情况）
    if (response && typeof response === 'object' && response !== null) {
      if ('fixtures' in response) {
        const fixturesValue = (response as any).fixtures;
        if (Array.isArray(fixturesValue)) {
          fixtures = fixturesValue;
          console.log('🔥🔥🔥 [前端分析] ✅✅✅ 成功提取fixtures，数量:', fixtures.length);
        } else {
          console.error('🔥🔥🔥 [前端分析] ❌ fixtures不是数组，类型:', typeof fixturesValue, '值:', fixturesValue);
        }
      } else {
        console.error('🔥🔥🔥 [前端分析] ❌ response中没有fixtures字段，response的键:', Object.keys(response));
      }
    } else {
      console.error('🔥🔥🔥 [前端分析] ❌ response不是有效对象，类型:', typeof response, '值:', response);
    }
    
    // 后端返回了空列表：多半是识别接口没识别出灯，或上游模型/渠道异常。请看后端终端「[分析]」日志。
    if (fixtures.length === 0) {
      console.warn(
        '[前端分析] 本次识别结果为空（fixtures=[]）。若图里明显有灯，请重启后端后再点识别，并看后端窗口的 [分析] 日志。',
      );
      console.warn('[前端分析] 接口原始返回:', JSON.stringify(response, null, 2));
    } else {
      console.log('🔥🔥🔥 [前端分析] ✅✅✅ 最终结果:', fixtures.length, '个光源');
      console.log('🔥🔥🔥 [前端分析] fixtures详情:', fixtures);
    }
    
    const identifyCount =
      response && typeof response === 'object' && 'identifyCount' in response
        ? Number((response as any).identifyCount)
        : undefined;
    const resTaskId =
      response && typeof response === 'object' && typeof (response as any).taskId === 'string'
        ? (response as any).taskId
        : undefined;

    return {
      fixtures,
      ...(Number.isFinite(identifyCount) ? { identifyCount } : {}),
      ...(resTaskId ? { taskId: resTaskId } : {}),
    };
  } catch (error) {
    console.error('🔥🔥🔥 [前端分析] ❌❌❌ 异常:', error);
    console.error('🔥🔥🔥 [前端分析] 错误消息:', (error as any)?.message);
    console.error('🔥🔥🔥 [前端分析] 错误堆栈:', (error as any)?.stack);
    throw error;
  }
};

/**
 * 生成参数化提示词
 */
function generatePrompt(
  intensity: number, 
  temperature: number, 
  colorValue?: number,
  rotation?: number,
  beamAngle?: number
): string {
  // 🔥 关键修复：确保强度、色温、颜色完全按照滑块值
  const intensityPercent = Math.round(intensity * 100);
  
  // 🔥 将色温 K 值映射为描述词（精确转换）
  // 前端 colorTemp 是 -100 到 100 的偏移值
  // 基准 6500K，-100 对应约 4500K（暖色），+100 对应约 8500K（冷色）
  const actualK = 6500 + (temperature * 20);
  let tempDesc = '';
  let tempType = '';
  if (actualK > 5500) {
    tempDesc = `cool blue-white daylight (${actualK.toFixed(0)}K)`;
    tempType = 'cool';
  } else if (actualK < 4500) {
    tempDesc = `warm golden amber (${actualK.toFixed(0)}K)`;
    tempType = 'warm';
  } else {
    tempDesc = `neutral pure white (${actualK.toFixed(0)}K)`;
    tempType = 'neutral';
  }
  
  // 🔥 颜色优先级：如果设置了色相（COLOR滑块），优先使用色相；否则使用色温
  let colorDesc = '';
  let useHue = false;
  if (colorValue !== undefined && colorValue !== null && colorValue !== 0) {
    useHue = true;
    // 🔥 精确的色相描述
    const hue = colorValue;
    let hueName = '';
    if (hue >= 0 && hue < 30) {
      hueName = hue < 15 ? 'red' : 'red-orange';
    } else if (hue >= 30 && hue < 60) {
      hueName = hue < 45 ? 'orange-red' : 'orange';
    } else if (hue >= 60 && hue < 90) {
      hueName = hue < 75 ? 'yellow-orange' : 'yellow';
    } else if (hue >= 90 && hue < 120) {
      hueName = hue < 105 ? 'yellow-green' : 'lime-green';
    } else if (hue >= 120 && hue < 150) {
      hueName = hue < 135 ? 'green' : 'green-cyan';
    } else if (hue >= 150 && hue < 180) {
      hueName = hue < 165 ? 'cyan-green' : 'cyan';
    } else if (hue >= 180 && hue < 210) {
      hueName = hue < 195 ? 'cyan-blue' : 'blue-cyan';
    } else if (hue >= 210 && hue < 240) {
      hueName = hue < 225 ? 'blue' : 'blue-indigo';
    } else if (hue >= 240 && hue < 270) {
      hueName = hue < 255 ? 'blue-purple' : 'purple-blue';
    } else if (hue >= 270 && hue < 300) {
      hueName = hue < 285 ? 'purple' : 'purple-magenta';
    } else if (hue >= 300 && hue < 330) {
      hueName = hue < 315 ? 'magenta' : 'magenta-pink';
    } else {
      hueName = hue < 345 ? 'pink' : 'pink-red';
    }
    colorDesc = `${hueName} (hue ${hue.toFixed(0)}°)`;
  }

  // 🔥 角度描述
  let angleDesc = '';
  if (rotation !== undefined && rotation !== null) {
    const getDirection = (angle: number): string => {
      const normalized = ((angle % 360) + 360) % 360;
      if (normalized >= 0 && normalized < 45) return 'right';
      if (normalized >= 45 && normalized < 135) return 'down';
      if (normalized >= 135 && normalized < 225) return 'left';
      if (normalized >= 225 && normalized < 315) return 'up';
      return 'right';
    };
    const direction = getDirection(rotation);
    angleDesc = `pointing ${direction} at ${rotation.toFixed(0)}°`;
  }

  const hueDegrees = colorValue ?? 0;

  // 🔥 组装最终指令 - 明确强调必须严格按照滑块值
  const colorPart = useHue 
    ? `⚠️⚠️⚠️ MANDATORY COLOR: ${colorDesc} - The light MUST be EXACTLY this color, matching the COLOR slider at ${hueDegrees.toFixed(0)}°.`
    : `⚠️⚠️⚠️ MANDATORY COLOR TEMPERATURE: ${tempDesc} - The light MUST be EXACTLY this temperature (${tempType} tone).`;
  
  const intensityPart = `⚠️⚠️⚠️ MANDATORY INTENSITY: ${intensityPercent}% brightness - The light MUST be EXACTLY this bright, matching the INTENSITY slider.`;
  const anglePart = angleDesc ? `⚠️⚠️⚠️ MANDATORY ANGLE: ${angleDesc} - The beam MUST point in this EXACT direction.` : '';
  const beamPart = beamAngle ? `⚠️⚠️⚠️ MANDATORY BEAM SPREAD: ${beamAngle.toFixed(0)}° - The beam spread MUST be EXACTLY this angle.` : '';
  
  return `${colorPart}
          ${intensityPart}
          ${anglePart}
          ${beamPart}
          
          ⚠️⚠️⚠️ CRITICAL: You will see colored guide areas in the image (semi-transparent colored sectors).
          These colored areas are LIGHTING GUIDES - they show exactly where and at what angle you must generate the light beam.
          ⚠️⚠️⚠️ MANDATORY: Transform the colored guide areas into realistic, high-intensity volumetric light beams.
          ⚠️⚠️⚠️ MANDATORY: The light beam MUST match the EXACT color/temperature shown in the colored guide area.
          ⚠️⚠️⚠️ MANDATORY: The light beam direction and angle MUST EXACTLY match the colored guide area's shape and direction.
          ⚠️⚠️⚠️ MANDATORY: The beam direction (${rotation !== undefined ? rotation.toFixed(0) + '°' : 'as shown in colored guide'}) and spread angle (${beamAngle !== undefined ? beamAngle.toFixed(0) + '°' : 'as shown in colored guide'}) MUST match the colored guide perfectly.
          ⚠️⚠️⚠️ MANDATORY: The intensity MUST match the slider value (${intensityPercent}%) - not brighter, not dimmer, EXACTLY ${intensityPercent}%.
          ⚠️⚠️⚠️ MANDATORY: The color/temperature MUST match the slider value EXACTLY - ${useHue ? `hue ${hueDegrees.toFixed(0)}°` : `${actualK.toFixed(0)}K ${tempType}`}.
          Blend the light naturally with the environment while maintaining the EXACT angle, color, and intensity of the colored guide area.
          High contrast, realistic ray-tracing, dramatic shadows, cinematic lighting. 
          Completely remove any existing light from the original image within the colored guide area, and replace it with the new light beam.`;
}

/**
 * 🔥 验证和修复图片格式，确保符合后端验证要求
 * 后端要求格式：data:image/(png|jpeg|jpg|webp|gif);base64,...
 */
export function validateAndFixImageFormat(base64Image: string): string {
  if (!base64Image || typeof base64Image !== 'string') {
    throw new Error('图片数据无效');
  }
  
  // 检查是否已经是正确格式
  const validFormatRegex = /^data:image\/(png|jpeg|jpg|webp|gif);base64,/i;
  if (validFormatRegex.test(base64Image)) {
    console.log('[图片格式验证] ✅ 图片格式正确');
    return base64Image;
  }
  
  console.warn('[图片格式验证] ⚠️  图片格式不符合要求，尝试修复...');
  console.warn('[图片格式验证] 原始格式:', base64Image.substring(0, 50));
  
  // 尝试修复格式
  let fixedImage = base64Image.trim();
  
  // 情况1：有 data:image/ 前缀但格式不对
  if (fixedImage.startsWith('data:image/')) {
    const parts = fixedImage.split(',');
    if (parts.length >= 2) {
      // 提取 base64 数据
      const base64Data = parts.slice(1).join(',');
      // 强制使用 PNG 格式（最兼容）
      fixedImage = `data:image/png;base64,${base64Data}`;
      console.log('[图片格式验证] 🔧 已修复：提取 base64 数据并添加 PNG 前缀');
    }
  }
  // 情况2：只有 base64 数据，没有前缀
  else if (fixedImage.length > 100 && !fixedImage.includes('data:')) {
    // 假设是 PNG 格式
    fixedImage = `data:image/png;base64,${fixedImage}`;
    console.log('[图片格式验证] 🔧 已修复：添加 PNG 前缀');
  }
  // 情况3：尝试从 Canvas 重新生成
  else {
    console.error('[图片格式验证] ❌ 无法修复图片格式');
    throw new Error('图片格式无效，无法修复。请确保图片是有效的 base64 编码。');
  }
  
  // 再次验证修复后的格式
  if (validFormatRegex.test(fixedImage)) {
    console.log('[图片格式验证] ✅ 修复成功，格式:', fixedImage.substring(0, 30));
    return fixedImage;
  } else {
    console.error('[图片格式验证] ❌ 修复失败，格式仍不正确');
    throw new Error('图片格式修复失败，请检查图片数据');
  }
}

export const renderEnhancedLighting = async (
  base64Image: string,
  fixtures: Fixture[],
  imageDimensions: { width: number; height: number; displayWidth?: number; displayHeight?: number },
  userEmail?: string,
  customInstruction?: string,
  commandMode?: boolean,
  referenceImage?: string,
  /** 与识别流程一致：传给后端多模态；蒙版前会对手动筒射灯用法线补采样 */
  spatialMaps?: {
    depthImage?: string | null;
    normalImage?: string | null;
    coordinateMeta?: SpatialCoordinateMeta | null;
  },
): Promise<{
  image: string;
  balance?: number;
  imageCredits?: number;
  cosUrl?: string;
  historyId?: string | number;
} | null> => {
  try {
    console.log('🔥🔥🔥 [前端渲染] ========== 开始调用渲染API ==========');
    console.log('[前端渲染] 图片尺寸:', imageDimensions);
    console.log('[前端渲染] 灯光数量:', fixtures.length);
    console.log('[前端渲染] 用户邮箱:', userEmail || '未提供');
    console.log('🔥🔥🔥 [前端渲染] 这是新版本的代码，包含蒙版生成功能！');
    
    // 🔥 关键修复：在开始处理前，先验证和修复原图格式
    let validatedBaseImage = validateAndFixImageFormat(base64Image);
    console.log('[前端渲染] ✅ 原图格式验证通过');
    
    // intensity 与侧栏一致：0–2（0–200%）；此前误夹到 1 会导致 >100% 无效
    const normalizedFixtures = fixtures.map(f => ({
      ...f,
      intensity: Math.max(0, Math.min(2, f.intensity)),
      colorTemp: Math.max(-100, Math.min(100, f.colorTemp || 0)), // 限制在 -100 到 100
      hue: f.hue !== undefined ? Math.max(0, Math.min(360, f.hue)) : undefined
    }));
    
    console.log('[前端渲染] 规范化后的灯光:', normalizedFixtures.filter(f => f.isModified).length, '个已修改');
    
    // 🔥 调试：输出所有灯光的类型和修改状态
    console.log('[前端渲染] 🔍 所有灯光详情:');
    normalizedFixtures.forEach((f, i) => {
      const spotlightInfo = f.type === 'spotlight' 
        ? `, rotation=${f.rotation ?? 'undefined'}, beamAngle=${f.beamAngle ?? 'undefined'}` 
        : '';
      console.log(`  [${i + 1}] id=${f.id}, type=${f.type}, isModified=${f.isModified}${spotlightInfo}`);
    });
    
    // 🔥 检查是否有spotlight类型的灯光需要做预处理涂抹
    const spotlights = normalizedFixtures.filter(f => f.type === 'spotlight');
    const modifiedSpotlights = normalizedFixtures.filter(f => f.type === 'spotlight' && f.isModified);
    console.log(`[前端渲染] 🔍 Spotlight统计: 总数=${spotlights.length}, 已修改=${modifiedSpotlights.length}`);
    
    // 仅自动识别灯允许预涂抹引导；手动“增加光源”统一走蒙版局部约束，减少位置漂移
    const hasSpotlights = normalizedFixtures.some(
      f => f.type === 'spotlight' && f.isModified && f.source !== 'manual'
    );
    console.log(`[前端渲染] 🔍 是否需要预处理涂抹: ${hasSpotlights ? '是' : '否'}`);
    // 蒙版：手动局部灯 + 识别槽灯调参（否则 auto cove 无蒙版、整图难出局部色温变化）
    const needsInpaintMask = normalizedFixtures.some(fixtureContributesToCombinedMask);
    console.log(`[前端渲染] 🔍 是否生成局部 inpaint 蒙版: ${needsInpaintMask ? '是' : '否'}`);
    
    // 🔥 关键修复：从 base64Image 中获取图片的真实尺寸，确保蒙版和预处理使用正确的尺寸
    let actualImageWidth = imageDimensions.width;
    let actualImageHeight = imageDimensions.height;
    
    // 🔥 从 base64Image 中获取真实尺寸
    try {
      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => {
          const naturalWidth = img.naturalWidth;
          const naturalHeight = img.naturalHeight;
          
          if (naturalWidth > 0 && naturalHeight > 0) {
            // 🔥 如果检测到的尺寸与传入的尺寸不一致，使用真实尺寸
            if (naturalWidth !== imageDimensions.width || naturalHeight !== imageDimensions.height) {
              console.warn(`[前端渲染] ⚠️  尺寸不匹配！传入尺寸: ${imageDimensions.width}x${imageDimensions.height}, 图片真实尺寸: ${naturalWidth}x${naturalHeight}`);
              console.warn(`[前端渲染] 🔧 使用图片真实尺寸: ${naturalWidth}x${naturalHeight}`);
              actualImageWidth = naturalWidth;
              actualImageHeight = naturalHeight;
            } else {
              console.log(`[前端渲染] ✅ 尺寸匹配：${naturalWidth}x${naturalHeight}`);
            }
          }
          resolve();
        };
        img.onerror = () => {
          console.warn('[前端渲染] ⚠️  无法从图片获取真实尺寸，使用传入的尺寸');
          resolve();
        };
        img.src = base64Image;
        // 超时保护
        setTimeout(() => {
          console.warn('[前端渲染] ⚠️  获取图片尺寸超时，使用传入的尺寸');
          resolve();
        }, 3000);
      });
    } catch (error) {
      console.warn('[前端渲染] ⚠️  获取图片尺寸失败，使用传入的尺寸:', error);
    }
    
    console.log(`[前端渲染] ✅ 最终使用的图片尺寸: ${actualImageWidth}x${actualImageHeight}`);

    const fixturesForRender = await enrichFixturesWithSpatialSamples(
      normalizedFixtures,
      spatialMaps?.depthImage,
      spatialMaps?.normalImage,
      spatialMaps?.coordinateMeta ?? undefined,
      actualImageWidth,
      actualImageHeight,
    );
    
    // 🔥 新方案：预处理涂抹引导 - 在原图上绘制半透明的彩色扇形引导色块
    let processedImage = validatedBaseImage; // 🔥 使用验证后的原图
    if (hasSpotlights) {
      console.log('[前端渲染] 🔥🔥🔥 使用预处理涂抹引导方案 🔥🔥🔥');
      console.log('[前端渲染] 检测到spotlight类型灯光，开始预处理涂抹...');
      console.log(`[前端渲染] 使用图片尺寸: ${actualImageWidth}x${actualImageHeight}`);
      
      // 🔥 输出所有spotlight的参数，用于调试
      const spotlights = normalizedFixtures.filter(f => f.type === 'spotlight' && f.isModified);
      console.log('[前端渲染] Spotlight列表:');
      spotlights.forEach(f => {
        const pixelX = (f.x / 1000) * actualImageWidth;
        const pixelY = (f.y / 1000) * actualImageHeight;
        const percentX = (pixelX / actualImageWidth) * 100;
        const percentY = (pixelY / actualImageHeight) * 100;
        console.log(`  - ${f.id}: 归一化坐标(${f.x}, ${f.y}) -> 像素坐标(${pixelX.toFixed(1)}, ${pixelY.toFixed(1)}) -> 百分比(${percentX.toFixed(1)}%, ${percentY.toFixed(1)}%), 角度=${f.rotation ?? 90}°, 光束角=${f.beamAngle ?? 30}°`);
        console.log(`    [坐标验证] 位置: ${percentX < 10 ? '左边缘' : percentX > 90 ? '右边缘' : '中间'}, ${percentY < 10 ? '上边缘' : percentY > 90 ? '下边缘' : '中间'}`);
      });
      
      try {
        // 🔥 关键步骤：在原图上绘制半透明的彩色扇形引导色块（使用真实尺寸）
        processedImage = await prePaintLightGuides(
          validatedBaseImage, // 🔥 使用验证后的原图
          normalizedFixtures,
          actualImageWidth,
          actualImageHeight
        );
        // 🔥 验证预处理后的图片格式
        processedImage = validateAndFixImageFormat(processedImage);
        console.log('[前端渲染] ✅ 预处理涂抹完成，处理后图片大小:', processedImage.length, '字符');
        console.log('[前端渲染] 🔥 处理后的图片已包含彩色引导色块，AI 将看到这些颜色并转换为真实光束');
      } catch (error) {
        console.error('[前端渲染] ❌ 预处理涂抹失败:', error);
        console.warn('[前端渲染] ⚠️  预处理失败，使用原图继续渲染');
        // 如果预处理失败，继续使用验证后的原图
        processedImage = validatedBaseImage;
      }
    }
    
    // 🔥 最终验证：确保发送给后端的图片格式正确
    processedImage = validateAndFixImageFormat(processedImage);
    console.log('[前端渲染] ✅ 最终图片格式验证通过，准备发送给后端');
    
    // 🔥 保留蒙版生成（用于调试和可能的其他用途）- 使用真实尺寸
    let maskImage: string | null = null;
    if (needsInpaintMask) {
      try {
        // 🔥 关键调试：在生成蒙版前，验证坐标系统
        console.log('[前端渲染] 🔍 蒙版生成前，验证坐标系统:');
        const spotlightsForMask = fixturesForRender.filter(fixtureContributesToCombinedMask);
        spotlightsForMask.forEach((f, i) => {
          const isNormalized = f.x <= 1000 && f.y <= 1000 && f.x >= 0 && f.y >= 0;
          const pixelX = (f.x / 1000) * actualImageWidth;
          const pixelY = (f.y / 1000) * actualImageHeight;
          console.log(`  [${i + 1}] id=${f.id}: 坐标=(${f.x}, ${f.y}), 是否归一化=${isNormalized ? '✅' : '❌'}, 像素坐标=(${pixelX.toFixed(1)}, ${pixelY.toFixed(1)})`);
          if (!isNormalized) {
            console.error(`  ❌ 错误：光源 ${f.id} 的坐标不是 0-1000 系统！`);
          }
        });
        
        maskImage = createCombinedMask(
          fixturesForRender,
          actualImageWidth,
          actualImageHeight
        );
        // 🔥 验证蒙版格式
        maskImage = validateAndFixImageFormat(maskImage);
        console.log('[前端渲染] ✅ 蒙版生成成功（用于调试），大小:', maskImage.length, '字符');
        console.log(`[前端渲染] ✅ 蒙版尺寸: ${actualImageWidth}x${actualImageHeight}（与原图一致）`);
        
        // 🔥 调试：将蒙版保存到全局变量
        if (typeof window !== 'undefined') {
          (window as any).__lastGeneratedMask = maskImage;
          (window as any).__lastMaskDimensions = { width: actualImageWidth, height: actualImageHeight };
          console.log('[前端渲染] 调试：蒙版已保存到 window.__lastGeneratedMask，可以在控制台查看');
          console.log(`[前端渲染] 调试：蒙版尺寸已保存到 window.__lastMaskDimensions: ${actualImageWidth}x${actualImageHeight}`);
        }
      } catch (error) {
        console.error('[前端渲染] ❌ 蒙版生成失败:', error);
      }
    }
    
    // 为每个spotlight生成参数化提示词
    const spotlightPrompts: { [key: string]: string } = {};
    normalizedFixtures
      .filter(f => f.type === 'spotlight' && f.isModified)
      .forEach(f => {
        spotlightPrompts[f.id] = generatePrompt(
          f.intensity,
          f.colorTemp || 0,
          f.hue,
          f.rotation, // 🔥 添加角度参数
          f.beamAngle // 🔥 添加光束角度参数
        );
      });
    
    // 只发送后端需要的字段
    const dimensions = {
      width: imageDimensions.width,
      height: imageDimensions.height
    };
    
    console.log('[前端渲染] 发送请求到后端...');
    console.log('[前端渲染] API_BASE_URL:', import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '(相对路径)' : '(开发环境)'));
    console.log('[前端渲染] 🔥 使用预处理后的图片（包含彩色引导色块）发送给后端');
    
    // 🔥 最终验证：确保发送的图片格式正确（双重保险）
    const finalImage = validateAndFixImageFormat(processedImage);
    console.log('[前端渲染] ✅ 最终图片格式验证:', finalImage.substring(0, 30));
    
    // 🔥 调试：输出色温参数，确保正确传递
    console.log('[前端渲染] 🔍 色温参数检查:');
    normalizedFixtures.filter(f => f.isModified).forEach(f => {
      console.log(`  - ${f.id} (${f.type}): colorTemp=${f.colorTemp}, hue=${f.hue ?? 'undefined'}, intensity=${f.intensity}`);
    });
    
    const response = await api.renderImage(
      finalImage, // 🔥 使用最终验证后的图片
      fixturesForRender,
      dimensions, 
      userEmail,
      maskImage, // 🔥 传递蒙版（用于调试，后端可能不使用）
      spotlightPrompts, // 🔥 传递参数化提示词
      undefined,
      customInstruction,
      commandMode,
      referenceImage,
      undefined,
      spatialMaps?.depthImage ?? undefined,
      spatialMaps?.normalImage ?? undefined,
    );
    
    console.log('[前端渲染] 收到后端响应:', {
      hasImage: !!response?.image,
      balance: response?.balance,
      imageCredits: response?.imageCredits,
      responseKeys: response ? Object.keys(response) : 'null'
    });
    
    if (response && response.image) {
      console.log('[前端渲染] ✅ 渲染成功，收到图片数据');
      console.log('[前端渲染] 后端返回余额:', response.balance, '剩余次数:', response.imageCredits);
      const cosUrl =
        typeof (response as any).cosUrl === 'string' && /^https?:\/\//i.test((response as any).cosUrl)
          ? (response as any).cosUrl
          : undefined;
      if (cosUrl) {
        console.log('[前端渲染] 历史/合并将优先使用 COS URL，避免与数据库双份');
      }
      return {
        image: response.image,
        balance: response.balance,
        imageCredits: response.imageCredits,
        cosUrl,
        historyId: (response as any).historyId ?? undefined,
      };
    } else {
      console.error('[前端渲染] ❌ 后端返回空结果');
      throw new Error('后端返回空结果');
    }
  } catch (error: any) {
    console.error("[前端渲染] ❌ 渲染图片失败:", error);
    
    // 提供更详细的错误信息
    if (error.message) {
      console.error("[前端渲染] 错误详情:", error.message);
    }
    
    throw error;
  }
};
